import { useState, useMemo, useCallback } from "react";

const STATUS_COLORS = {
  todo: { bg: "#EFF6FF", border: "#0055FF", text: "#0055FF", dot: "#0055FF" },
  in_progress: { bg: "#FFFBEB", border: "#D97706", text: "#92400E", dot: "#D97706" },
  review: { bg: "#F5F3FF", border: "#7C3AED", text: "#5B21B6", dot: "#7C3AED" },
  done: { bg: "#F0FDF4", border: "#16A34A", text: "#166534", dot: "#16A34A" },
};

const STATUS_LABELS = {
  todo: "To Do", in_progress: "In Progress", review: "Review", done: "Done",
};

const CRITICAL_COLOR = "#DC2626";

function buildDAG(tasks) {
  const taskMap = {};
  tasks.forEach(t => { taskMap[t.id] = t; });

  const levels = {};
  const dependents = {};
  tasks.forEach(t => {
    (t.dependencies || []).forEach(depId => {
      if (!dependents[depId]) dependents[depId] = [];
      dependents[depId].push(t.id);
    });
  });

  const roots = tasks.filter(t => !t.dependencies || t.dependencies.length === 0);
  roots.forEach(t => { levels[t.id] = 0; });

  const queue = [...roots.map(t => t.id)];
  const visited = new Set(queue);

  while (queue.length > 0) {
    const current = queue.shift();
    const children = dependents[current] || [];
    children.forEach(childId => {
      const childTask = taskMap[childId];
      if (!childTask) return;
      const parentLevels = (childTask.dependencies || [])
        .map(pid => levels[pid])
        .filter(l => l !== undefined);
      if (parentLevels.length === (childTask.dependencies || []).length) {
        levels[childId] = Math.max(...parentLevels) + 1;
        if (!visited.has(childId)) {
          visited.add(childId);
          queue.push(childId);
        }
      }
    });
  }

  tasks.forEach(t => {
    if (levels[t.id] === undefined) levels[t.id] = 0;
  });

  const levelGroups = {};
  Object.entries(levels).forEach(([id, level]) => {
    if (!levelGroups[level]) levelGroups[level] = [];
    levelGroups[level].push(id);
  });

  const maxLevel = Math.max(0, ...Object.values(levels));
  return { taskMap, levels, levelGroups, maxLevel, dependents };
}

/**
 * Critical Path Method (CPM):
 * Forward pass: calculate earliest start/finish for each task.
 * Backward pass: calculate latest start/finish for each task.
 * Tasks with zero total float (latest_start - earliest_start = 0) are on the critical path.
 */
function computeCriticalPath(tasks) {
  if (!tasks || tasks.length === 0) return { nodes: new Set(), edges: new Set() };

  const taskMap = {};
  tasks.forEach(t => { taskMap[t.id] = t; });

  const dependents = {};
  tasks.forEach(t => {
    (t.dependencies || []).forEach(depId => {
      if (!dependents[depId]) dependents[depId] = [];
      dependents[depId].push(t.id);
    });
  });

  // Task duration: use duration_days if available, otherwise calculate from dates, fallback to 1
  function getDuration(t) {
    if (t.duration_days && t.duration_days > 0) return t.duration_days;
    if (t.start_date && t.end_date) {
      const s = new Date(t.start_date);
      const e = new Date(t.end_date);
      const diff = Math.ceil((e - s) / (1000 * 60 * 60 * 24));
      return diff > 0 ? diff : 1;
    }
    return 1;
  }

  // Forward pass — earliest start (ES) and earliest finish (EF)
  const ES = {}, EF = {};
  const roots = tasks.filter(t => !t.dependencies || t.dependencies.length === 0);

  // Topological order via Kahn's algorithm
  const inDegree = {};
  tasks.forEach(t => { inDegree[t.id] = (t.dependencies || []).filter(d => taskMap[d]).length; });
  const topoQueue = roots.map(t => t.id);
  const topoOrder = [];

  while (topoQueue.length > 0) {
    const id = topoQueue.shift();
    topoOrder.push(id);
    (dependents[id] || []).forEach(childId => {
      inDegree[childId]--;
      if (inDegree[childId] === 0) topoQueue.push(childId);
    });
  }

  // Forward pass
  topoOrder.forEach(id => {
    const task = taskMap[id];
    const deps = (task.dependencies || []).filter(d => taskMap[d]);
    ES[id] = deps.length > 0 ? Math.max(...deps.map(d => EF[d] || 0)) : 0;
    EF[id] = ES[id] + getDuration(task);
  });

  // Project duration
  const projectDuration = Math.max(0, ...Object.values(EF));

  // Backward pass — latest finish (LF) and latest start (LS)
  const LF = {}, LS = {};
  const leaves = tasks.filter(t => !(dependents[t.id] && dependents[t.id].length > 0));
  leaves.forEach(t => { LF[t.id] = projectDuration; });

  // Reverse topological order
  for (let i = topoOrder.length - 1; i >= 0; i--) {
    const id = topoOrder[i];
    const task = taskMap[id];
    const children = (dependents[id] || []).filter(d => taskMap[d]);
    if (LF[id] === undefined) {
      LF[id] = children.length > 0 ? Math.min(...children.map(c => LS[c] !== undefined ? LS[c] : projectDuration)) : projectDuration;
    }
    LS[id] = LF[id] - getDuration(task);
  }

  // Critical path: tasks with zero float (ES === LS)
  const criticalNodes = new Set();
  const criticalEdges = new Set();

  topoOrder.forEach(id => {
    const totalFloat = (LS[id] || 0) - (ES[id] || 0);
    if (Math.abs(totalFloat) < 0.001) {
      criticalNodes.add(id);
    }
  });

  // Critical edges: both source and target on critical path
  tasks.forEach(t => {
    if (criticalNodes.has(t.id)) {
      (t.dependencies || []).forEach(depId => {
        if (criticalNodes.has(depId)) {
          criticalEdges.add(`${depId}->${t.id}`);
        }
      });
    }
  });

  return { nodes: criticalNodes, edges: criticalEdges };
}

function getChainTasks(taskId, tasks) {
  const taskMap = {};
  tasks.forEach(t => { taskMap[t.id] = t; });
  const chain = new Set();

  // Upstream
  const upQueue = [taskId];
  while (upQueue.length > 0) {
    const id = upQueue.shift();
    if (chain.has(id)) continue;
    chain.add(id);
    const task = taskMap[id];
    if (task) {
      (task.dependencies || []).forEach(depId => {
        if (!chain.has(depId)) upQueue.push(depId);
      });
    }
  }

  // Downstream
  const dependents = {};
  tasks.forEach(t => {
    (t.dependencies || []).forEach(depId => {
      if (!dependents[depId]) dependents[depId] = [];
      dependents[depId].push(t.id);
    });
  });
  const downQueue = [taskId];
  while (downQueue.length > 0) {
    const id = downQueue.shift();
    if (chain.has(id) && id !== taskId) continue;
    chain.add(id);
    (dependents[id] || []).forEach(childId => {
      if (!chain.has(childId)) downQueue.push(childId);
    });
  }

  return chain;
}

function NodeBox({ x, y, task, isFocused, isCritical, isCollapsed, onToggle, onClick, mini }) {
  const colors = STATUS_COLORS[task.status] || STATUS_COLORS.todo;
  const w = mini ? 140 : 180;
  const h = mini ? 40 : 52;
  const titleMaxLen = mini ? 16 : 22;
  const title = task.title.length > titleMaxLen ? task.title.slice(0, titleMaxLen) + "..." : task.title;

  const borderColor = isCritical && !isFocused ? CRITICAL_COLOR : colors.border;
  const strokeW = isFocused ? 2 : (isCritical ? 2 : 1);

  return (
    <g
      transform={`translate(${x}, ${y})`}
      style={{ cursor: "pointer" }}
      onClick={() => onClick?.(task)}
      data-testid={`dep-node-${task.id}`}
    >
      {/* Critical glow */}
      {isCritical && !isFocused && (
        <rect x={-2} y={-2} width={w + 4} height={h + 4} rx={5} fill="none" stroke={CRITICAL_COLOR} strokeWidth={1} opacity={0.25} />
      )}
      {/* Shadow */}
      <rect x={1} y={2} width={w} height={h} rx={3} fill="rgba(0,0,0,0.04)" />
      {/* Main box */}
      <rect
        width={w} height={h} rx={3}
        fill={isFocused ? colors.border : colors.bg}
        stroke={borderColor}
        strokeWidth={strokeW}
        opacity={isFocused ? 1 : 0.95}
      />
      {/* Status dot */}
      <circle cx={12} cy={mini ? 14 : 16} r={4} fill={isFocused ? "#fff" : colors.dot} />
      {/* Critical path badge (small red diamond) */}
      {isCritical && !mini && !isFocused && (
        <g transform={`translate(${w - 28}, 6)`}>
          <polygon points="6,0 12,6 6,12 0,6" fill={CRITICAL_COLOR} opacity={0.8} />
        </g>
      )}
      {/* Title */}
      <text
        x={22} y={mini ? 18 : 20}
        fontSize={mini ? 10 : 11}
        fontWeight={isFocused ? 700 : 600}
        fill={isFocused ? "#fff" : colors.text}
        fontFamily="'Helvetica Neue', Arial, sans-serif"
      >
        {title}
      </text>
      {/* Task number / status */}
      {!mini && (
        <text x={22} y={38} fontSize={9} fill={isFocused ? "rgba(255,255,255,0.7)" : "#A3A3A3"} fontFamily="'Helvetica Neue', Arial, sans-serif">
          {task.task_number || ""} {task.task_number ? "·" : ""} {STATUS_LABELS[task.status] || task.status}
        </text>
      )}
      {mini && (
        <text x={22} y={34} fontSize={8} fill={isFocused ? "rgba(255,255,255,0.7)" : "#A3A3A3"} fontFamily="'Helvetica Neue', Arial, sans-serif">
          {STATUS_LABELS[task.status] || task.status}
        </text>
      )}
      {/* Collapse toggle indicator */}
      {onToggle && (
        <g transform={`translate(${w - 16}, ${h / 2 - 6})`} onClick={e => { e.stopPropagation(); onToggle(task.id); }}>
          <rect x={-2} y={-2} width={16} height={16} fill="transparent" />
          {isCollapsed ? (
            <path d="M4 2 L10 7 L4 12" stroke={isFocused ? "#fff" : "#A3A3A3"} fill="none" strokeWidth={1.5} />
          ) : (
            <path d="M2 4 L7 10 L12 4" stroke={isFocused ? "#fff" : "#A3A3A3"} fill="none" strokeWidth={1.5} />
          )}
        </g>
      )}
    </g>
  );
}

function Arrow({ x1, y1, x2, y2, highlighted, critical }) {
  const midX = (x1 + x2) / 2;
  const d = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
  const color = critical ? CRITICAL_COLOR : (highlighted ? "#0055FF" : "#D4D4D4");
  const width = critical ? 2 : (highlighted ? 1.5 : 1);
  const opacity = critical ? 0.85 : (highlighted ? 0.9 : 0.5);

  return (
    <>
      <path d={d} fill="none" stroke={color} strokeWidth={width} opacity={opacity} />
      <polygon
        points={`${x2},${y2} ${x2 - 6},${y2 - 3} ${x2 - 6},${y2 + 3}`}
        fill={color}
        opacity={opacity}
      />
    </>
  );
}

export default function DependencyGraph({ tasks = [], focusTaskId = null, onTaskClick, mini = false }) {
  const [collapsed, setCollapsed] = useState({});
  const [showCriticalPath, setShowCriticalPath] = useState(!mini);

  const toggleCollapse = useCallback((taskId) => {
    setCollapsed(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  }, []);

  const relevantTasks = useMemo(() => {
    if (!tasks || tasks.length === 0) return [];
    const hasRelation = new Set();
    tasks.forEach(t => {
      if (t.dependencies && t.dependencies.length > 0) {
        hasRelation.add(t.id);
        t.dependencies.forEach(d => hasRelation.add(d));
      }
    });
    if (mini && focusTaskId) {
      const chain = getChainTasks(focusTaskId, tasks);
      return tasks.filter(t => chain.has(t.id));
    }
    return tasks.filter(t => hasRelation.has(t.id));
  }, [tasks, mini, focusTaskId]);

  const focusChain = useMemo(() => {
    if (!focusTaskId) return new Set();
    return getChainTasks(focusTaskId, tasks);
  }, [focusTaskId, tasks]);

  const criticalPath = useMemo(() => {
    if (!showCriticalPath) return { nodes: new Set(), edges: new Set() };
    return computeCriticalPath(relevantTasks);
  }, [relevantTasks, showCriticalPath]);

  const dag = useMemo(() => buildDAG(relevantTasks), [relevantTasks]);

  const layout = useMemo(() => {
    const nodeW = mini ? 140 : 180;
    const nodeH = mini ? 40 : 52;
    const colGap = mini ? 40 : 60;
    const rowGap = mini ? 12 : 18;
    const padX = mini ? 16 : 30;
    const padY = mini ? 16 : 30;

    const positions = {};
    const { levelGroups, maxLevel, taskMap } = dag;

    const visibleInLevel = {};
    for (let lvl = 0; lvl <= maxLevel; lvl++) {
      const ids = levelGroups[lvl] || [];
      visibleInLevel[lvl] = ids.filter(id => {
        const task = taskMap[id];
        if (!task) return false;
        for (const depId of (task.dependencies || [])) {
          if (collapsed[depId]) return false;
        }
        return true;
      });
    }

    let maxRowCount = 0;
    for (let lvl = 0; lvl <= maxLevel; lvl++) {
      const ids = visibleInLevel[lvl] || [];
      maxRowCount = Math.max(maxRowCount, ids.length);
      ids.forEach((id, idx) => {
        positions[id] = {
          x: padX + lvl * (nodeW + colGap),
          y: padY + idx * (nodeH + rowGap),
        };
      });
    }

    const totalW = padX * 2 + (maxLevel + 1) * nodeW + maxLevel * colGap;
    const totalH = padY * 2 + maxRowCount * nodeH + (maxRowCount - 1) * rowGap;

    return { positions, totalW: Math.max(totalW, 200), totalH: Math.max(totalH, 80), nodeW, nodeH };
  }, [dag, collapsed, mini]);

  if (relevantTasks.length === 0) {
    return (
      <div className="flex items-center justify-center py-8 text-[#A3A3A3] text-xs" data-testid="dep-graph-empty">
        {mini ? "No dependencies" : "No task dependencies in this project. Add dependencies between tasks to see the dependency graph."}
      </div>
    );
  }

  const { positions, totalW, totalH, nodeW, nodeH } = layout;

  const arrows = [];
  relevantTasks.forEach(task => {
    (task.dependencies || []).forEach(depId => {
      const from = positions[depId];
      const to = positions[task.id];
      if (from && to) {
        const highlighted = focusTaskId && focusChain.has(task.id) && focusChain.has(depId);
        const critical = showCriticalPath && criticalPath.edges.has(`${depId}->${task.id}`);
        arrows.push({
          key: `${depId}-${task.id}`,
          x1: from.x + nodeW,
          y1: from.y + nodeH / 2,
          x2: to.x,
          y2: to.y + nodeH / 2,
          highlighted: highlighted && !critical,
          critical,
        });
      }
    });
  });

  const hasDependents = {};
  relevantTasks.forEach(t => {
    (t.dependencies || []).forEach(depId => {
      hasDependents[depId] = true;
    });
  });

  const criticalCount = criticalPath.nodes.size;

  return (
    <div data-testid="dep-graph-wrapper">
      {/* Critical path toggle — full mode only */}
      {!mini && (
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={() => setShowCriticalPath(p => !p)}
            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-bold uppercase tracking-wider border rounded transition-colors ${
              showCriticalPath
                ? "bg-[#FEF2F2] border-[#DC2626] text-[#DC2626]"
                : "bg-white border-[#E5E5E5] text-[#A3A3A3] hover:border-[#DC2626] hover:text-[#DC2626]"
            }`}
            data-testid="critical-path-toggle"
          >
            <svg width="10" height="10" viewBox="0 0 10 10">
              <polygon points="5,0 10,5 5,10 0,5" fill={showCriticalPath ? CRITICAL_COLOR : "#A3A3A3"} />
            </svg>
            Critical Path {showCriticalPath && criticalCount > 0 ? `(${criticalCount} tasks)` : ""}
          </button>
          {showCriticalPath && criticalCount > 0 && (
            <span className="text-[10px] text-[#DC2626] font-medium" data-testid="critical-path-info">
              Delays on these tasks directly impact the project deadline
            </span>
          )}
        </div>
      )}
      <div
        className={mini ? "overflow-x-auto" : "overflow-auto border border-[#E5E5E5] rounded bg-white"}
        style={mini ? {} : { maxHeight: 520 }}
        data-testid="dep-graph-container"
      >
        <svg
          width={totalW}
          height={totalH}
          viewBox={`0 0 ${totalW} ${totalH}`}
          style={{ minWidth: totalW, display: "block" }}
        >
          {!mini && (
            <defs>
              <pattern id="dep-dots" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="0.5" fill="#E5E5E5" />
              </pattern>
            </defs>
          )}
          {!mini && <rect width={totalW} height={totalH} fill="url(#dep-dots)" />}

          {/* Arrows — non-critical first, then critical on top */}
          <g>
            {arrows.filter(a => !a.critical).map(({ key, ...arrowProps }) => (
              <Arrow key={key} {...arrowProps} />
            ))}
            {arrows.filter(a => a.critical).map(({ key, ...arrowProps }) => (
              <Arrow key={key} {...arrowProps} />
            ))}
          </g>

          {/* Nodes */}
          <g>
            {relevantTasks.map(task => {
              const pos = positions[task.id];
              if (!pos) return null;
              return (
                <NodeBox
                  key={task.id}
                  x={pos.x}
                  y={pos.y}
                  task={task}
                  isFocused={focusTaskId === task.id}
                  isCritical={showCriticalPath && criticalPath.nodes.has(task.id)}
                  isCollapsed={!!collapsed[task.id]}
                  onToggle={hasDependents[task.id] ? toggleCollapse : null}
                  onClick={onTaskClick}
                  mini={mini}
                />
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
}

export function DependencyGraphLegend() {
  return (
    <div className="flex flex-wrap items-center gap-4 text-[10px] text-[#A3A3A3] uppercase tracking-[0.15em] font-bold" data-testid="dep-graph-legend">
      {Object.entries(STATUS_COLORS).map(([key, colors]) => (
        <div key={key} className="flex items-center gap-1.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colors.dot }} />
          <span>{STATUS_LABELS[key]}</span>
        </div>
      ))}
      <div className="flex items-center gap-1.5 ml-2">
        <svg width="12" height="12" viewBox="0 0 12 12"><polygon points="6,0 12,6 6,12 0,6" fill={CRITICAL_COLOR} /></svg>
        <span>Critical Path</span>
      </div>
    </div>
  );
}
